import React, { useEffect } from 'react';
import { useLocatorLov } from '../hooks/useLocatorLov';

interface EditableCellProps {
  column: string;
  value: string;
  lineId: string;
  line: any;
  onUpdate: (lineId: string, field: string, value: string) => void;
  isDarkTheme?: boolean;
  selectedHeaderToOrg?: string;
  subinventories: string[];
  refetchSubinventories?: (orgId: string) => void;
  refetchLocators?: (orgId: string, subinventory: string) => void;
}

const EditableCell: React.FC<EditableCellProps> = ({
  column,
  value,
  lineId,
  line,
  onUpdate,
  isDarkTheme,
  selectedHeaderToOrg,
  subinventories,
  refetchSubinventories,
  refetchLocators
}) => {
  const { data: locators, refetch: refetchLocatorsHook } = useLocatorLov(
    selectedHeaderToOrg, 
    line.toSubinventory, 
    false
  );

  // Auto-fetch locators when line has subinventory but locators array is empty
  useEffect(() => {
    if (selectedHeaderToOrg && line.toSubinventory && locators.length === 0 && column === 'To Locator') {
      refetchLocatorsHook(selectedHeaderToOrg, line.toSubinventory);
    }
  }, [selectedHeaderToOrg, line.toSubinventory, locators.length, column, refetchLocatorsHook]);


  const baseStyle = {
    width: '100%',
    padding: '4px',
    border: `1px solid ${isDarkTheme ? '#555' : '#ccc'}`,
    backgroundColor: isDarkTheme ? '#333' : '#fff',
    color: isDarkTheme ? '#fff' : '#000',
    fontSize: '12px',
    boxSizing: 'border-box' as const
  };

  if (column === 'To Subinventory') {
    // Check if API originally returned a value (not user-entered)
    // If line has returnLineId AND the original API response had toSubinventory value
    const isApiPreFilled = line.returnLineId && line.originalToSubinventory;
    if (isApiPreFilled) {
      return <div style={{ minHeight: '16px', padding: '4px' }}>{line.toSubinventory}</div>;
    }
    // Show dropdown if API returned NULL/empty (user can edit)
    return (
      <select
        value={value}
        onChange={(e) => {
          const newSubinventory = e.target.value;
          console.log('Subinventory changed:', newSubinventory, 'for line:', lineId);
          onUpdate(lineId, column, newSubinventory);
          // Clear locator when subinventory changes
          if (line.toLocator) {
            onUpdate(lineId, 'To Locator', '');
          }
          // Immediately fetch locators for new subinventory
          if (selectedHeaderToOrg && newSubinventory && refetchLocators) {
            refetchLocators(selectedHeaderToOrg, newSubinventory);
          }
        }}
        onFocus={() => {
          if (selectedHeaderToOrg && subinventories.length === 0 && refetchSubinventories) {
            refetchSubinventories(selectedHeaderToOrg);
          }
        }}
        style={baseStyle}
      >
        <option value="">Select subinventory</option>
        {/* Add current value if not in subinventories list (for scanned values) */}
        {value && !subinventories.includes(value) && (
          <option key={value} value={value}>
            {value}
          </option>
        )}
        {subinventories.map((subinventory) => (
          <option key={subinventory} value={subinventory}>
            {subinventory}
          </option>
        ))}
      </select>
    );
  }

  if (column === 'To Locator') {

    // Check if API originally returned a value (not user-entered)
    // If line has returnLineId AND the original API response had toLocator value
    // BUT if isSerialValid is true, it means user scanned and should remain editable
    const isApiPreFilled = line.returnLineId && line.originalToLocator && !line.isSerialValid;
    if (isApiPreFilled) {
      return <div style={{ minHeight: '16px', padding: '4px' }}>{line.toLocator}</div>;
    }
    // Show dropdown if API returned NULL/empty (user can edit)
    return (
      <select
        value={value}
        onChange={(e) => onUpdate(lineId, column, e.target.value)}
        onFocus={() => {
          if (selectedHeaderToOrg && line.toSubinventory && locators.length === 0) {
            refetchLocatorsHook(selectedHeaderToOrg, line.toSubinventory);
          }
        }}

        disabled={!line.toSubinventory}
        style={baseStyle}
      >
        <option value="">Select locator</option>
        {locators.map((locator) => (
          <option key={locator} value={locator}>
            {locator}
          </option>
        ))}
      </select>
    );
  }

  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onUpdate(lineId, column, e.target.value)}
      placeholder={`Enter ${column.toLowerCase()}`}
      style={baseStyle}
    />
  );
};

export default EditableCell;